import { Helmet } from "react-helmet-async";

const steps = [
  {
    image: "/Dashboard/user.png",
    alt: "Create account",
    title: "Create a ToothSketch account",
    description: "Sign up to get started with our platform",
  },
  {
    image: "/Dashboard/upload-to-cloud.png",
    alt: "Upload cases",
    title: "Easy case upload via our portal",
    description: "Upload your cases quickly and securely",
  },
  {
    image: "/Dashboard/timer.png",
    alt: "Real-time status",
    title: "View real-time status of your case",
    description: "Track progress in real-time",
  },
  {
    image: "/Dashboard/online-support--v1.png",
    alt: "Communication",
    title: "Communicate directly with the design team",
    description: "Direct communication channel",
  },
  {
    image: "/Dashboard/documents-folder.png",
    alt: "Completed cases",
    title: "Receive completed cases in your dashboard",
    description: "Access your finished work",
  },
];

const Login = ({
  handleGoogleLogin,
  rel,
}: {
  handleGoogleLogin: () => void;
  rel: string | null;
}) => {
  const partnerText = rel || "";
  const isUris = partnerText === "uris";
  return (
    <>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      <div
        className={`h-full flex flex-col items-center py-12 ${
          isUris ? "border-t-2 border-[#c31c30] bg-[#1a1b1f]" : "bg-[#F4F4F2]"
        }`}
      >
        <div className="w-full max-w-4xl mx-auto text-center">
          <div className="space-y-4">
            {partnerText === "elemdig" ? (
              <div className="flex flex-col gap-4">
                <div className="w-full flex justify-center">
                  <img
                    src="/elemlogo2.png"
                    alt="Element Digital"
                    className="w-60 h-16"
                  />
                </div>
                <h1 className="text-md text-gray-900 font-normal">
                  <span className="font-bold">Element Digital </span>
                  brings to you integrated designing & milling solutions for
                  your lab.
                </h1>
                <div className="flex flex-col md:flex-row w-full h-full gap-2 items-center justify-center">
                  <p className="text-gray-400 text-md">powered By</p>
                  <img
                    src="/logoWhite.png"
                    alt="Toothsketch LLP"
                    className="w-24 h-8"
                  />
                </div>
              </div>
            ) : partnerText === "uris" ? (
              <div className="flex flex-col gap-4">
                <div className="w-full flex justify-center">
                  <img
                    src="/Uris%20Logo.png"
                    alt="Uris Dental"
                    className="h-16 w-auto max-w-xs object-contain"
                  />
                </div>
                <h1 className="text-md text-white font-normal">
                  <span className="font-bold">Uris Dental </span>
                  - Dental Laboratory
                </h1>
                <div className="flex flex-col md:flex-row w-full h-full gap-2 items-center justify-center">
                  <p className="text-gray-400 text-md">powered By</p>
                  <img
                    src="/logo.png"
                    alt="Toothsketch LLP"
                    className="w-24 h-8 object-contain"
                  />
                </div>
              </div>
            ) : partnerText === "bant" ? (
              <div className="flex flex-col gap-4">
                <div className="w-full flex justify-center">
                  <img
                    src="/bantlogo.svg"
                    alt="Bant Dental"
                    className="w-80 h-16"
                  />
                </div>
                <h1 className="text-md text-gray-900 font-normal">
                  <span className="font-bold">Bant Dental </span> - Digital
                  Dental Laboratory | Private Denture Clinic
                </h1>
                <div className="flex flex-col md:flex-row w-full h-full gap-2 items-center justify-center">
                  <p className="text-gray-400 text-md">powered By</p>
                  <img
                    src="/logoWhite.png"
                    alt="Toothsketch LLP"
                    className="w-24 h-8"
                  />
                </div>
              </div>
            ) : (
              <h1 className="text-5xl font-bold text-gray-900">Get Started!</h1>
            )}

            <p
              className={`text-md max-w-md mx-auto ${
                isUris ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Just follow these simple steps to get started!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 my-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`cursor-pointer rounded-xl p-6 text-center space-y-4 ${
                  isUris
                    ? "border border-[#2a2b31] bg-[#1d1e22] text-gray-300"
                    : "border-2 border-[#DDDDD8] bg-white text-[#555552]"
                }`}
              >
                <div className="flex justify-center">
                  <div className="w-16 h-12 bg-white rounded-full flex items-center justify-center overflow-hidden">
                    <img
                      src={step.image || "/placeholder.svg"}
                      alt={step.alt}
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3
                    className={`font-semibold text-sm leading-tight ${
                      isUris ? "text-gray-300" : "text-[#888888]"
                    }`}
                  >
                    {step.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6 md:space-y-4">
            <button
              onClick={handleGoogleLogin}
              className="bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 px-8 py-3 text-base font-medium rounded-full transition-colors duration-200 flex items-center justify-center mx-auto"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Login to <span className="font-semibold ml-1">Continue</span>
            </button>

            <div
              className={`text-sm max-w-2xl mx-auto ${
                isUris ? "text-gray-400" : "text-gray-600"
              }`}
            >
              <p>
                You will be redirected to our Google portal. Note: You{" "}
                <span
                  className={`font-semibold ${
                    isUris ? "text-white" : "text-gray-900"
                  }`}
                >
                  do not
                </span>{" "}
                need a GMail account.
              </p>
              <p>
                For non-GMail IDs please select{" "}
                <span className="italic">
                  "Use my current email address instead"
                </span>{" "}
                and then proceed with{" "}
                <span
                  className={`font-semibold ${
                    isUris ? "text-white" : "text-gray-900"
                  }`}
                >
                  any email of your choice
                </span>
                .
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div
              className={`flex items-center justify-center space-x-2 text-sm ${
                isUris ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <span>Powered by</span>
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="font-medium">Google Cloud Platform</span>
              </div>
            </div>

            <p className={`text-xs ${isUris ? "text-gray-400" : "text-gray-500"}`}>
              Trouble logging in? Call us at +44 788 234 7988 or +1 845 762 6241
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
